import { Container, Box, Flex, Grid } from 'components/box';
import {
  SectionHeading,
  SmallHeader,
  Text,
  ParagraphText,
  Heading,
} from 'components/typography';
import { css } from '@emotion/react';
import resourceUrl from '../util/make-url';
import { prepMetaTags } from '../util/seo';
import config from '../util/load-config';
import { GetStaticPropsResult } from 'next';
import { PageProps } from 'types/types';
import { breakpointsPx } from 'themes/onedayonly';
import { Link } from 'components/configurable-routing';
import { canonicalUrl } from '../util/url';
import Button from 'components/button';
import { useContext } from 'react';
import { NewsletterModalContext } from 'contexts/newsletter-modal';

const ICON_WIDTH = 250;

interface CategoryIcon {
  id: string;
  url: string;
  link: string;
}

const categoryIcons: CategoryIcon[] = [
  {
    id: 'apparel-accessories',
    url: resourceUrl('odo/black-friday/icons-2025/apparel-accessories.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/apparel-and-accessories',
  },
  {
    id: 'health-beauty',
    url: resourceUrl('odo/black-friday/icons-2025/health-beauty.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/health-and-beauty',
  },
  {
    id: 'electronics',
    url: resourceUrl('odo/black-friday/icons-2025/electronics.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/electronics',
  },
  {
    id: 'food-beverages',
    url: resourceUrl('odo/black-friday/icons-2025/food-beverages.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/food-and-beverages',
  },
  {
    id: 'furniture',
    url: resourceUrl('odo/black-friday/icons-2025/furniture.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/furniture',
  },
  {
    id: 'cameras-and-optics',
    url: resourceUrl('odo/black-friday/icons-2025/camera-optics.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/cameras-and-optics',
  },
  {
    id: 'home-and-garden',
    url: resourceUrl('odo/black-friday/icons-2025/home-garden.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/home-and-garden',
  },
  {
    id: 'sporting-goods',
    url: resourceUrl('odo/black-friday/icons-2025/sporting-goods.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/sporting-goods',
  },
  {
    id: 'baby-and-toddler',
    url: resourceUrl('odo/black-friday/icons-2025/baby-toddler.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/baby-and-toddler',
  },
  {
    id: 'hardware',
    url: resourceUrl('odo/black-friday/icons-2025/hardware.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/hardware',
  },
  {
    id: 'luggage-and-bags',
    url: resourceUrl('odo/black-friday/icons-2025/luggage-bags.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/luggage-and-bags',
  },
  {
    id: 'animals-and-pets',
    url: resourceUrl('odo/black-friday/icons-2025/animals-pet-supplies.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
    link: '/category/animals-and-pet-supplies',
  },
];

const BEST_BF_DEALS_URL =
  'https://blog.onedayonly.co.za/black-friday-shopping-tips-get-the-best-deals-on-the-coolest-tech-and-small-gifts/';

const BlackFriday = () => {
  const { openNewsletterModal } = useContext(NewsletterModalContext);

  return (
    <Container py={3}>
      <picture>
        <source
          srcSet={resourceUrl('odo/black-friday/banners-2025/mobile-v2.jpg', {
            isAssetUrl: true,
            width: 500,
          })}
          media={`(max-width: ${breakpointsPx[0] - 1}px)`}
        />
        <source
          srcSet={resourceUrl('odo/black-friday/banners-2025/tablet-v2.jpg', {
            isAssetUrl: true,
            width: 900,
          })}
          media={`(min-width: ${breakpointsPx[0]}px) and (max-width: ${
            breakpointsPx[1] - 1
          }px)`}
        />
        <img
          css={theme => css`
            border-radius: ${theme.space[2]}px;
            width: 100%;
          `}
          src={resourceUrl('odo/black-friday/banners-2025/desktop-v2.jpg', {
            width: 1400,
            isAssetUrl: true,
          })}
          alt="Black Friday Banner"
        />
      </picture>
      <Box>
        <Heading as="h1" mt={[3, 4]}>
          Shop Black Friday Deals
        </Heading>
        <SmallHeader as="h2" mt={[3, 4]}>
          OneDayOnly Black Friday: Up to 100% OFF Verified Deal Drops
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']} mb={2}>
          Brace yourself! Ahead of{' '}
          <Link
            target="_blank"
            href="https://blog.onedayonly.co.za/black-friday-101-tips-to-navigate-the-chaos-like-a-pro/"
            fontWeight={500}
          >
            Black Friday
          </Link>
          , we&apos;ve tested gravity and it turns out prices can, in fact, fall
          further! This Black Friday, <strong>up to 100% OFF</strong> deals will
          drop randomly throughout the day, every day from 21 November to 1
          December! Yep, you read that right - some of our Black Friday deals
          are literally FREE! Now that&apos;s what we call unreal deals.
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mb={2}>
          Every day, one (or more) jaw-dropping Deals will drop at random times,
          starting 21 November 2025. But blink and you&apos;ll miss it - stock
          is seriously limited.
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mb={2} fontWeight="bold">
          Want in?
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mb={2}>
          Make sure you&apos;ve got the{' '}
          <Link target="_blank" href="https://onedayonly.go.link/6CsPm">
            OneDayOnly app downloaded
          </Link>
          , enable push notifications,{' '}
          <strong>sign up to our newsletter</strong> to be the first to get the
          tea, and <strong>follow us on socials</strong> for updates. Because
          when a Verified Deal drops, it&apos;s fastest fingers first!
        </ParagraphText>

        <SmallHeader as="h2" mt={[3, 4]}>
          What is Black Friday?
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']} mb={2}>
          Once upon a time in the United States, Black Friday was the day after
          Thanksgiving – leftover turkey, family drama, the works. These days,
          it&apos;s a worldwide holiday for your wallet that merrily announces
          the start of the{' '}
          <Link
            target="_blank"
            href="https://blog.onedayonly.co.za/best-discount-shopping-tips-for-this-festive-season-from-onedayonly/"
            fontWeight={500}
          >
            festive shopping season
          </Link>
          , with retailers offering massive savings on the brands and products
          you&apos;ve been eyeing all year.
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']}>
          OneDayOnly? We&apos;re no different. Sure, we&apos;re already known
          for offering Black Friday deals online 365 days a year, but that
          doesn&apos;t mean we&apos;re not going to pull out all the stops to
          make you feel like an even bigger deal than ever before this Black
          Friday.
        </ParagraphText>

        <Grid
          gridTemplateColumns="1fr 1fr 1fr"
          gridColumnGap={4}
          gridRowGap={4}
        >
          <Link
            href="https://onedayonly.go.link/6CsPm"
            target="_blank"
            rel="noopener noreferrer"
            maxWidth="100%"
            alignItems="center"
            mt={[3, 4]}
            css={css`
              text-decoration: none;
              display: flex;
              flex-direction: column;
              align-items: center;
              margin-top: 1.5rem;
            `}
          >
            <img
              css={css`
                width: 200px;
                max-width: 100%;
                border-radius: 10%;
              `}
              src={resourceUrl('odo/black-friday/icons-2025/app-download.svg', {
                width: ICON_WIDTH,
                isAssetUrl: true,
              })}
              alt="Download the app"
            />
            <Text
              textAlign="center"
              fontSize={[1, 2]}
              fontWeight="bold"
              mt="10px"
            >
              Download the app
            </Text>
          </Link>

          <Flex
            maxWidth="100%"
            flexDirection="column"
            alignItems="center"
            mt={[3, 4]}
            onClick={openNewsletterModal}
            css={css`
              cursor: pointer;
            `}
          >
            <img
              css={css`
                width: 200px;
                max-width: 100%;
                border-radius: 10%;
              `}
              src={resourceUrl(
                'odo/black-friday/icons-2025/email-subscribe.svg',
                {
                  width: ICON_WIDTH,
                  isAssetUrl: true,
                }
              )}
              alt="Sign up to our newsletter"
            />
            <Button
              variant="minimal"
              color="primary"
              hoverColor="none"
              mt="8px"
              textTransform="none"
              fontSize={[1, 2]}
              css={theme => css`
                font-family: ${theme.fonts.body};
                letter-spacing: 0px;
              `}
            >
              Sign up to our newsletter
            </Button>
          </Flex>

          <Link
            href={config.social.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            maxWidth="100%"
            alignItems="center"
            mt={[3, 4]}
            css={css`
              text-decoration: none;
              display: flex;
              flex-direction: column;
              align-items: center;
              margin-top: 1.5rem;
            `}
          >
            <img
              css={css`
                width: 200px;
                max-width: 100%;
                border-radius: 10%;
              `}
              src={resourceUrl('odo/black-friday/icons-2025/social-media.svg', {
                width: ICON_WIDTH,
                isAssetUrl: true,
              })}
              alt="Follow us on socials"
            />
            <Text
              textAlign="center"
              fontSize={[1, 2]}
              fontWeight="bold"
              mt="10px"
            >
              Follow us on socials
            </Text>
          </Link>
        </Grid>

        <SmallHeader as="h2" mt={[3, 4]}>
          Become a OneDayOnly Fan
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']}>
          Black Friday is coming - and OneDayOnly Fans get access to{' '}
          <strong>exclusive deal drops, sneak peeks, offers, and more</strong>.
          Think jaw-dropping discounts and VIP-only surprises that never make it
          to the public site.
        </ParagraphText>

        <ParagraphText
          width={['95%', null, '100%']}
          mt={[1, 2]}
          fontWeight="bold"
        >
          How it works:
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
          Becoming a OneDayOnly Fan isn&apos;t about luck - it&apos;s about
          loyalty. Keep opening, browsing, and shopping to stay on the list. The
          more you engage, the more epic your access.
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
          Stock is limited and moves fast, so keep your eyes on your app and
          inbox. When a VIP-only deal drops,{' '}
          <strong>hit “quick add to cart”</strong> and checkout like a pro -
          because it&apos;s always fastest fingers first.
        </ParagraphText>

        <SmallHeader as="h2" mt={[3, 4]}>
          All the right appliances
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']}>
          There&apos;s no hiding it, we got A to Z Black Friday shopping deals,
          which means that there&apos;s something for everyone, whatever your
          vibe. Unbelievable{' '}
          <Link target="_blank" href={config.site.url} fontWeight={500}>
            discounts
          </Link>{' '}
          on A-grade appliances just so happen to be on everyone&apos;s list.
          We&apos;re talking everything small but mighty, like air fryers,
          kettles, robot vacuum cleaners – all of it. And don&apos;t forget
          about the bigger bangs for your buck, like fridges, washing machines,
          dishwashers – the lot.
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
          Ready to kit out your space with ridiculous Black Friday specials for
          the whole house? Find signature furniture pieces, including beds,
          mattresses, headboards, lounge suits, and even patio furniture.
          Between the bedroom bargains and outdoor finds, you&apos;ll be ready
          for the best summer yet this{' '}
          <Link
            target="_blank"
            href="https://blog.onedayonly.co.za/last-minute-christmas-hacks-from-onedayonly/"
            fontWeight={500}
          >
            December
          </Link>
          .
        </ParagraphText>

        <SmallHeader as="h2" mt={[3, 4]}>
          Fashion that fits
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']}>
          In need of a style dealer? This{' '}
          <Link
            target="_blank"
            href="https://blog.onedayonly.co.za/black-friday-101-tips-to-navigate-the-chaos-like-a-pro/"
            fontWeight={500}
          >
            Black Friday
          </Link>{' '}
          is set to be the most stylish one yet with big savings on the fashion,
          apparel and accessories brands that will fit any budget. Try on Black
          Friday deals on everything, from running shoes and sneakers to leather
          jackets, jeans, jerseys and more. Look your best for less, be it at
          the gym or out on the town.
        </ParagraphText>

        <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
          While you&apos;re at it, it just makes scents to browse scroll-worthy
          deals on perfumes you&apos;re probably obsessed with. The{' '}
          <Link target="_blank" href={BEST_BF_DEALS_URL} fontWeight={500}>
            best Black Friday deals
          </Link>{' '}
          are fragrantly yours to check out, then checkout (if you&apos;re quick
          enough). Smell that? It&apos;s the biggest Black Friday specials
          you&apos;ve been waiting for all year.
        </ParagraphText>

        <SmallHeader as="h2" mt={[3, 4]}>
          All hands on tech
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']}>
          You can&apos;t shop Black Friday deals online without getting wired up
          for Cyber Monday deals. Cache in on must-have tech gadgets and
          powered-up savings. Upgrade your setup with unbeatable Black Friday
          deals, from popular electronics and gaming equipment to a big
          selection of wearables and smart technology. You&apos;re cord-ially
          invited to save BIG this Black Friday – run, don&apos;t walk past the
          checkout line.
        </ParagraphText>

        <SmallHeader as="h2" mt={[3, 4]}>
          Skip the mall, shop it all online
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']}>
          Life gets busy and going to the store can be chaotic between parking
          and queues. Online shopping isn&apos;t a luxury anymore, but a
          necessity. So do your Black Friday deal-grabbing from the couch, on
          the move or wherever you are with the{' '}
          <Link
            target="_blank"
            href="https://onedayonly.go.link/5CaN5"
            fontWeight={500}
          >
            OneDayOnly app
          </Link>
          . Haven&apos;t downloaded it yet? Do it now and turn on your push
          notifications so you never miss a drop – not just Black Friday deals,
          all of them. This time of year, things can get wild. How wild, you
          wonder? Up to a whopping 100% OFF. That&apos;s right, we don&apos;t
          just slash prices – sometimes, we straight-up give stuff away. The
          savings are so ridiculous, we have to say it twice: download the app
          for all your Black Friday{' '}
          <Link target="_blank" href={config.site.url} fontWeight={500}>
            deal
          </Link>{' '}
          fixes as they drop.
        </ParagraphText>

        <SmallHeader as="h2" mt={[3, 4]}>
          What else can I shop on OneDayOnly?
        </SmallHeader>
        <ParagraphText width={['95%', null, '100%']}>
          We offer a wide range of online deals on{' '}
          <Link target="_blank" href="/category/electronics" fontWeight={500}>
            electronics
          </Link>
          ,{' '}
          <Link
            target="_blank"
            href="/category/apparel-and-accessories"
            fontWeight={500}
          >
            clothing
          </Link>
          ,{' '}
          <Link
            target="_blank"
            href="/category/health-and-beauty"
            fontWeight={500}
          >
            beauty products
          </Link>
          ,{' '}
          <Link target="_blank" href="/category/furniture" fontWeight={500}>
            furniture
          </Link>
          , and accessories. Shop top international brands like{' '}
          <Link target="_blank" href="/brands/philips" fontWeight={500}>
            Philips
          </Link>
          ,{' '}
          <Link target="_blank" href="/brands/crocs" fontWeight={500}>
            Crocs
          </Link>
          ,{' '}
          <Link target="_blank" href="/brands/dyson" fontWeight={500}>
            Dyson
          </Link>
          ,{' '}
          <Link target="_blank" href="/brands/ugg" fontWeight={500}>
            UGG
          </Link>{' '}
          and{' '}
          <Link target="_blank" href="/brands" fontWeight={500}>
            more
          </Link>
          , all with the best deals and{' '}
          <Link target="_blank" href="/clearance-sale" fontWeight={500}>
            clearance sales
          </Link>{' '}
          in South Africa.
        </ParagraphText>
      </Box>

      {/* catagories we sell*/}
      <Box mb={3}>
        <Box mb={2} mt={3}>
          <SectionHeading
            textAlign="start"
            py={3}
            fontSize="3"
            marginBottom={0}
          >
            Shop BIG DEALS from our most popular categories:
          </SectionHeading>
        </Box>
        <Grid
          gridTemplateColumns={['1fr 1fr', '1fr 1fr 1fr', '1fr 1fr 1fr 1fr']}
          gridColumnGap={4}
          gridRowGap={4}
        >
          {categoryIcons.map(category => (
            <Flex
              key={category.id}
              maxWidth="100%"
              flexDirection="column"
              alignItems="center"
            >
              <Link
                href={category.link}
                target="_blank"
                css={css`
                  text-decoration: none;
                  transition: transform 0.2s ease;
                  &:hover {
                    transform: scale(1.05);
                  }
                `}
              >
                <img
                  css={css`
                    width: ${ICON_WIDTH}px;
                    max-width: 100%;
                    border-radius: 10%;
                  `}
                  src={category.url}
                  alt={category.id}
                />
              </Link>
            </Flex>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export async function getStaticProps(): Promise<
  GetStaticPropsResult<PageProps>
> {
  const meta = config.seo.meta.pages['black-friday'];

  return {
    props: {
      layout: {
        metaTags: {
          ...(meta
            ? {
                ...prepMetaTags(meta),
                canonicalUrl: canonicalUrl('black-friday'),
              }
            : { canonicalUrl: canonicalUrl('black-friday') }),
        },
      },
    },
  };
}

export default BlackFriday;
